import React, { useState } from 'react';
import {
  CustomizationField,
  CustomizationFieldType,
  CustomizationData,
} from '../../types/customization';
import { storageService } from '../../services/storageService';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../utils/constants';
import { toast } from 'react-toastify';

interface ProductCustomizationProps {
  fields: CustomizationField[];
  onCustomizationChange: (data: CustomizationData) => void;
}

const ProductCustomization: React.FC<ProductCustomizationProps> = ({
  fields,
  onCustomizationChange,
}) => {
  const { currentUser } = useAuth();
  const [customizationData, setCustomizationData] = useState<CustomizationData>({});
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set());

  const handleTextChange = (label: string, value: string) => {
    const updated = { ...customizationData, [label]: value };
    setCustomizationData(updated);
    onCustomizationChange(updated);
  };

  const handleFileUpload = async (label: string, file: File) => {
    // Check if user is logged in
    if (!currentUser) {
      toast.error('Please log in to upload files');
      return;
    }

    // Mark field as uploading
    setUploadingFields((prev) => new Set(prev).add(label));

    try {
      // Upload to Firebase Storage
      const downloadUrl = await storageService.uploadCustomizationImage(file, file.name);

      if (downloadUrl) {
        // Store download URL in customization data
        const updated = { ...customizationData, [label]: downloadUrl };
        setCustomizationData(updated);
        onCustomizationChange(updated);
        toast.success('✅ File uploaded successfully!');
      } else {
        toast.error('❌ Failed to upload file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('❌ Error uploading file');
    } finally {
      // Remove uploading state
      setUploadingFields((prev) => {
        const next = new Set(prev);
        next.delete(label);
        return next;
      });
    }
  };

  const renderField = (field: CustomizationField) => {
    const isUploading = uploadingFields.has(field.label);
    const hasFile = !!customizationData[field.label];

    switch (field.type) {
      case CustomizationFieldType.TEXT:
        return (
          <div key={field.label} style={styles.fieldContainer}>
            <label style={styles.label}>{field.label}</label>
            <input
              type="text"
              style={styles.textInput}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              onChange={(e) => handleTextChange(field.label, e.target.value)}
              value={customizationData[field.label] || ''}
            />
          </div>
        );

      case CustomizationFieldType.FILE_UPLOAD:
        return (
          <div key={field.label} style={styles.fieldContainer}>
            <label style={styles.label}>{field.label}</label>
            <div style={styles.fileUploadContainer}>
              <input
                type="file"
                id={`file-${field.label}`}
                style={styles.fileInput}
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(field.label, file);
                  }
                }}
                disabled={isUploading}
              />
              <label
                htmlFor={`file-${field.label}`}
                style={{
                  ...styles.fileLabel,
                  ...(isUploading ? styles.fileLabelDisabled : {}),
                  ...(hasFile ? styles.fileLabelSuccess : {}),
                }}
              >
                {isUploading ? (
                  <>
                    <span style={styles.icon}>⏳</span>
                    Uploading...
                  </>
                ) : hasFile ? (
                  <>
                    <span style={styles.icon}>✅</span>
                    File uploaded
                  </>
                ) : (
                  <>
                    <span style={styles.icon}>📤</span>
                    Upload File
                  </>
                )}
              </label>
              {hasFile && !isUploading && (
                <div style={styles.successMessage}>
                  ✓ File uploaded to cloud storage
                </div>
              )}
            </div>
          </div>
        );

      case CustomizationFieldType.IMAGE_SELECTION:
        // Not implemented - hide this field type
        return null;

      default:
        return null;
    }
  };

  if (fields.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>
        <span style={styles.titleIcon}>✏️</span>
        Personalize Your Product
      </h3>
      <div style={styles.fieldsContainer}>{fields.map(renderField)}</div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: colors.backgroundWhite,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '12px',
    padding: '20px',
    marginTop: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  titleIcon: {
    fontSize: '20px',
  },
  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.textDark,
  },
  textInput: {
    padding: '12px 16px',
    fontSize: '15px',
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'Montserrat, sans-serif',
  },
  fileUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fileInput: {
    display: 'none',
  },
  fileLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: colors.backgroundWhite,
    border: `2px solid ${colors.primaryGold}`,
    borderRadius: '8px',
    color: colors.primaryGold,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    justifyContent: 'center',
  },
  fileLabelDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    borderColor: colors.borderLight,
    color: colors.textGray,
  },
  fileLabelSuccess: {
    borderColor: '#10B981',
    color: '#10B981',
  },
  icon: {
    fontSize: '18px',
  },
  successMessage: {
    fontSize: '13px',
    color: '#10B981',
    fontWeight: '500',
  },
};

export default ProductCustomization;
